import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { MoreHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SettingsSection } from './-components/settings-section';
import type { UserRole } from '@minty/shared/user';
import type { TFunction } from 'i18next';
import { can } from '@minty/shared/permissions';
import {
  clinicRoles,
  lastManagerError,
  noRolesError,
} from '@minty/shared/user';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { Skeleton } from '~/components/ui/skeleton';
import { appErrorCode } from '~/lib/app-error';
import { notify } from '~/lib/toast';
import { useTRPC } from '~/lib/trpc';

export const Route = createFileRoute('/_dashboard/settings/users')({
  pendingComponent: SettingsUsersPending,
  component: SettingsUsersRoute,
});

function SettingsUsersRoute() {
  const trpc = useTRPC();
  const { data: currentUser } = useSuspenseQuery(
    trpc.auth.session.getCurrentUser.queryOptions(),
  );

  return <UsersList canEditRoles={can(currentUser.roles, 'roles')} />;
}

/** Named where they are shown. The role names themselves are the domain's, and
 *  stay in English wherever they are stored or compared. */
function roleLabel(role: UserRole, t: TFunction<'settings'>) {
  switch (role) {
    case 'manager':
      return t('Manager', { ns: 'settings' });
    case 'receptionist':
      return t('Receptionist', { ns: 'settings' });
    case 'observer':
      return t('Observer', { ns: 'settings' });
    default:
      return role;
  }
}

/** No special case for your own row: you may take a second role, and the only
 *  move that could strand the app — the last manager dropping `manager` — is
 *  refused for everyone by the same rule. */
function UsersList({ canEditRoles }: { canEditRoles: boolean }) {
  const { t } = useTranslation('settings');
  const trpc = useTRPC();
  const { data: users } = useSuspenseQuery(
    trpc.users.management.list.queryOptions(),
  );
  const { mutateAsync: setRole } = useMutation(
    trpc.users.management.setRole.mutationOptions(),
  );

  /**
   * Toggle one role, and send the whole set that results.
   *
   * The server takes the set rather than a delta, so two admins editing at once
   * cannot compose into a state neither of them chose — the later save wins
   * whole, instead of half of each landing.
   */
  async function handleToggleRole(
    userId: string,
    roles: ReadonlyArray<UserRole>,
    role: UserRole,
  ) {
    const wanted = roles.includes(role)
      ? roles.filter((held) => held !== role)
      : [...roles, role];

    try {
      await setRole({
        userId,
        roles: wanted,
      });
      notify.success(t('Role updated.'));
    } catch (error) {
      /** The two refusals worth naming: everything else is a failed save. */
      const reason = appErrorCode(error);

      if (reason === lastManagerError) {
        notify.error(t('There has to be at least one manager.'));
        return;
      }

      if (reason === noRolesError) {
        notify.error(t('Everyone needs at least one role.'));
        return;
      }

      notify.error(t('Unable to update role.'));
    }
  }

  return (
    <SettingsSection.Wrapper>
      <SettingsSection.Header>
        <SettingsSection.Title>{t('Users')}</SettingsSection.Title>
        <SettingsSection.Description>
          {t('Manage system users')}
        </SettingsSection.Description>
      </SettingsSection.Header>

      <SettingsSection.ContentWrapper className="border-b-0">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-border">
                <th className="p-2 text-left text-sm font-medium text-muted-foreground">
                  {t('User')}
                </th>
                <th className="p-2 text-left text-sm font-medium text-muted-foreground">
                  {t('Role')}
                </th>
                {canEditRoles && (
                  <th className="p-2 text-right text-sm font-medium text-muted-foreground">
                    {t('Actions')}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                /** The last manager cannot be demoted — by anyone, themselves
                 *  included. The server refuses it too; this is so the option
                 *  does not read as available. */
                const isLastManager =
                  user.roles.includes('manager') &&
                  users.filter((other) => other.roles.includes('manager'))
                    .length === 1;

                return (
                  <tr
                    key={user.id}
                    className="border-b border-border last:border-b-0"
                  >
                    <td className="p-2">
                      <div className="text-sm font-medium text-foreground">
                        {user.name}
                      </div>
                      {user.email && (
                        <div className="text-sm text-muted-foreground">
                          {user.email}
                        </div>
                      )}
                    </td>
                    <td className="p-2">
                      {/* One badge per role held, so somebody with both looks
                          like somebody with both. */}
                      <div className="flex flex-wrap gap-1">
                        {user.roles.map((role) => (
                          <Badge
                            key={role}
                            tone={role === 'manager' ? 'primary' : 'neutral'}
                          >
                            {roleLabel(role, t)}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    {canEditRoles && (
                      <td className="p-2 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon-sm"
                              aria-label={t('Actions')}
                            >
                              <MoreHorizontal
                                className="size-4"
                                strokeWidth={2.5}
                              />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            {([...clinicRoles, 'admin'] as const).map(
                              (role) => {
                                const held = user.roles.includes(role);
                                /** Keep at least one manager in the clinic. */
                                const wouldStrandUs =
                                  role === 'manager' && held && isLastManager;

                                return (
                                  <DropdownMenuCheckboxItem
                                    key={role}
                                    checked={held}
                                    disabled={wouldStrandUs}
                                    /** Radix closes on select and the menu shows
                                     *  several independent toggles; keeping it open
                                     *  lets both be set in one visit. */
                                    onSelect={(event) => event.preventDefault()}
                                    onCheckedChange={() =>
                                      handleToggleRole(
                                        user.id,
                                        user.roles,
                                        role,
                                      )
                                    }
                                  >
                                    {roleLabel(role, t)}
                                  </DropdownMenuCheckboxItem>
                                );
                              },
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SettingsSection.ContentWrapper>
    </SettingsSection.Wrapper>
  );
}

function SettingsUsersPending() {
  const { t } = useTranslation('settings');

  return (
    <SettingsSection.Wrapper aria-busy="true">
      <SettingsSection.Header>
        <SettingsSection.Title>{t('Users')}</SettingsSection.Title>
        <SettingsSection.Description>
          {t('Manage system users')}
        </SettingsSection.Description>
      </SettingsSection.Header>
      <SettingsSection.ContentWrapper className="border-b-0">
        <div className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      </SettingsSection.ContentWrapper>
    </SettingsSection.Wrapper>
  );
}
